import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { isAllowedByScopes, scopesFor } from 'src/app/auth/services/role-scope';
import { routePathOf } from 'src/app/auth/utils/return-url';
import { OfflineModeState } from './offline-mode.state';

/**
 * Where offline mode lands, and where any other route is redirected to while it lasts: Intelligence >
 * Scan (product owner, 12/10/2026: "apriamolo su scan" -- at a fair the phone opens to scan cards),
 * for an identity whose roles open Scan; otherwise the printings browse (`OFFLINE_CATALOG_HOME`).
 * Resolve it per identity with `offlineHomeFor`.
 */
export const OFFLINE_HOME = '/app/ingest/scan';

/** The offline landing for an identity that may use the catalogue but not Scan. */
export const OFFLINE_CATALOG_HOME = '/app/ingest/printings';

/**
 * The only route trees offline mode admits: the two read-only catalogue browses and their details,
 * and Intelligence > Scan (spec 2026-10-08 section 3.4: a tab booted with no network scans with the
 * games downloaded in its Modalita' offline; same roles as the catalogue).
 */
const OFFLINE_ROUTE_ROOTS = ['/app/ingest/printings', '/app/ingest/sealed', '/app/ingest/scan'];

/**
 * The roles that may open the catalogue -- a COPY of `CATALOG_ROLES` (ingest-routing.module.ts),
 * because importing that constant from here would pull the lazy ingest module's component graph into
 * the main bundle. `offline-routes.spec.ts` asserts the two stay equal, so the copy cannot drift.
 */
export const OFFLINE_CATALOG_ROLES = [
  'user',
  'supervisor',
  'admin',
  'ingest-operator',
  'ingest_operator',
  // the event role reads the catalogue online (CATALOG_ROLES), so offline too (spec 2026-10-08 3.2)
  'intelligence-viewer',
];

/**
 * The roles that open Intelligence > Scan -- a COPY of the scan route's `data.roles`
 * (ingest-routing.module.ts, `CATALOG_ROLES` today), for the same bundle reason as above;
 * `offline-routes.spec.ts` asserts it equals the route's own list.
 */
export const OFFLINE_SCAN_ROLES = [...OFFLINE_CATALOG_ROLES];

/** True for a URL inside the offline route trees (query string and fragment ignored). */
export function isOfflineAllowedUrl(url: string | null | undefined): boolean {
  if (!url) {
    return false;
  }
  const path = routePathOf(url).replace(/\/+$/, '');
  return OFFLINE_ROUTE_ROOTS.some((root) => path === root || path.startsWith(root + '/'));
}

/**
 * Whether a URL is one offline mode admits for these roles: inside the offline route trees AND
 * allowed by the confined roles' scopes (ROLE_SCOPES), the confinement RoleScopeGuard applies online.
 * Review of 24/10/2026: ['trainee', 'user'] online is kept out of Intelligence by the trainee scope,
 * offline it opened the whole catalogue because only the route roles were checked.
 */
export function isOfflineAllowedFor(roles: readonly string[] | null | undefined, url: string | null | undefined): boolean {
  return isOfflineAllowedUrl(url) && isAllowedByScopes(scopesFor(roles), url ?? '');
}

/** Whether an identity's roles may open the offline catalogue at all (a tree its scopes allow too). */
export function canUseOfflineCatalog(roles: readonly string[] | null | undefined): boolean {
  return (
    (roles ?? []).some((r) => OFFLINE_CATALOG_ROLES.includes(r)) &&
    OFFLINE_ROUTE_ROOTS.some((root) => isOfflineAllowedFor(roles, root))
  );
}

/** Whether an identity's roles open Intelligence > Scan (its scopes included). */
export function canUseOfflineScan(roles: readonly string[] | null | undefined): boolean {
  return (roles ?? []).some((r) => OFFLINE_SCAN_ROLES.includes(r)) && isOfflineAllowedFor(roles, OFFLINE_HOME);
}

/** Where offline mode lands for these roles: Scan when they open it, else the printings browse. */
export function offlineHomeFor(roles: readonly string[] | null | undefined): string {
  return canUseOfflineScan(roles) ? OFFLINE_HOME : OFFLINE_CATALOG_HOME;
}

/**
 * On the `auth` route: while offline mode lasts the login screen cannot do anything (there is no
 * server to log into), so the tab is sent to the offline home instead (`offlineHomeFor`: Scan, or the
 * printings browse for roles that do not open Scan). Cold boots land here too -- the catch-all route
 * redirects to `auth` -- which is how an offline boot at `/` reaches Scan.
 */
export const offlineAuthRedirectGuard: CanActivateFn = () => {
  const offline = inject(OfflineModeState);
  return offline.active ? inject(Router).parseUrl(offlineHomeFor(offline.identity?.roles)) : true;
};
